<template>
  <VPopup
    :target="target"
    :visible="true"
    class="qty-popup"
    show-pointer
    position="bottom"
  >
    <div class="qty-popup-content">
      <VInputNumberRusV2
        ref="qtyInput"
        v-model="qty"
        class="qty-popup-content-input"
        :type="Qty"
        @keydown.enter="saveQty"
      >
        <template #helper-text>
          <div class="document-id-wrap">
            <div class="label">
              Номер фактуры:
            </div>
            <div class="value">
              {{ cell?.SWOracleDocumentId ?? '--' }}
            </div>
          </div>
          <VButton
            theme="link"
            :disabled="GlobalLoader.isVisible"
            @click="showProductCellHistory"
          >
            История товара {{ monthLabel }}
          </VButton>
        </template>
      </VInputNumberRusV2>

      <div class="qty-popup-content-buttons-group">
        <VButton
          theme="primary"
          :disabled="GlobalLoader.isVisible"
          @click="saveQty"
        >
          {{ cell !== undefined ? 'Сохранить' : 'Создать' }}
        </VButton>
        <VButton @click="$core.dialogs.ProductionPlansChangeQtyPopup.hide()">
          Отмена
        </VButton>
      </div>
    </div>
  </VPopup>
</template>

<style lang="scss">
.mds-popup.qty-popup {
	width: 250px;
	padding: 10px;

	.qty-popup-content {
		display: flex;
		flex-direction: column;
		gap: 10px;

		&-input {
			.inner {
				.helper-text {
					display: flex;
					flex-direction: column;
					justify-content: space-between;
					font-size: 13px;
					gap: 5px;

					.document-id-wrap {
						display: flex;
						flex-direction: row;

						> .label {
							color: var(--base-strong);
						}

						> .value {
							padding-left: 5px;
							color: var(--faint-text-strong);
						}
					}

				}
			}
		}

		&-buttons-group {
			display: flex;
			justify-content: space-between;
		}
	}
}
</style>

<script lang="ts">
import Qty                    from '@/core/types/Qty';
import DateSimple             from '@/core/types/DateSimple';
import GlobalLoader           from '@/core/services/GlobalLoader/GlobalLoader';
import ProductionPlansService from '@/domains/ProductionPlans/ProductionPlansService';
import ProductionPlanProduct  from '@/domains/ProductionPlans/ProductionPlanProduct';

export default defineComponent({
  name: 'ProductionPlansChangeQtyPopup',

  props: {
    /** Ячейка грида, под которой рисуется попап */
    target: {
      type:     Object as PropType<HTMLElement>,
      required: true,
    },
    row: {
      type:     Object as PropType<ProductionPlanProduct>,
      required: true,
    },
    /** Ключ месяца в row.qtyByDates, например '2026-04-01' */
    monthKey: {
      type:     String,
      required: true,
    },
  },

  emits: ['show-product-cell-history'],

  setup() {
    return { Qty, GlobalLoader };
  },

  data() {
    return {
      qty: Qty.zero(),
    };
  },

  /** Попап открывается заново на каждую ячейку — начальное значение берём при монтировании */
  mounted() {
    this.qty = this.cell?.qty ?? Qty.zero();

    this.$nextTick(() => {
      this.$refs.qtyInput?.$el?.querySelector('input')?.focus();
    });
  },

  computed: {
    /** Запись плана на месяц; undefined — на этот месяц позиции ещё нет */
    cell(): NonNullable<ProductionPlanProduct['qtyByDates']>[string] | undefined {
      return this.row.qtyByDates?.[this.monthKey];
    },

    /** Подпись месяца для кнопки истории, например «за Апрель 2026» */
    monthLabel(): string {
      const date = ProductionPlansService.formatMonthLabel(this.monthKey);
      return `за ${date.month} ${date.year}`;
    },
  },

  methods: {
    /** Открывает историю ячейки и закрывает попап */
    showProductCellHistory(): void {
      this.$emit('show-product-cell-history');
      this.$core.dialogs.ProductionPlansChangeQtyPopup.hide();
    },

    /** Сохраняет количество: правит существующую позицию месяца или создаёт новую */
    async saveQty(): Promise<void> {
      if (this.cell !== undefined) {
        await this.updateQty();
      }
      else {
        await this.createQty();
      }
    },

    /** Правка существующей позиции. Значение в строке меняем сразу, при ошибке откатываем */
    async updateQty(): Promise<void> {
      const cell    = this.cell!;
      const prevQty = cell.qty;

      cell.qty = this.qty;
      this.$core.dialogs.ProductionPlansChangeQtyPopup.hide();

      const isUpdated = await ProductionPlansService.update({ id: Number(cell.id), qty: this.qty });

      if (isUpdated !== true) {
        cell.qty = prevQty;
      }
    },

    /** Создание позиции на месяц. Строку дополняем сразу, при ошибке убираем запись */
    async createQty(): Promise<void> {
      const qtyByDates = this.row.qtyByDates!;

      this.$core.dialogs.ReportsLoadingDialog.show({ title: 'создание позиции в плане' });

      qtyByDates[this.monthKey] = {
        SWOracleDocumentId: null,
        id:                 null,
        plannedArrivalDate: new DateSimple(this.monthKey),
        qty:                this.qty,
      };

      const isAdded = await ProductionPlansService.addProducts({
        productionSiteId: this.row.productionSite!.id,
        productCode:      this.row.code,
        data:             [{ plannedArrivalDate: this.monthKey, qty: this.qty }],
      });

      this.$core.dialogs.ReportsLoadingDialog.hide();

      if (isAdded !== true) {
        delete qtyByDates[this.monthKey];
        this.$core.showNotification.error('Ошибка при добавлении позиции');
      }
    },
  },
});
</script>
